import { inject, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';

/**
 * Logging level indicates priority for logging. 1 is the highest priority
 * and will always be logged, unless logging is entirely disabled.
 * Each level above will only log if the service level is greater than or
 * equal to it.
 **/
export enum LoggingLevel {
  DISABLED = 9999,
  FATAL = 10,
  ERROR = 20,
  WARN = 30,
  LOG_1 = 100,
  LOG_2 = 200,
  LOG_3 = 300,
}

/** Logging service. */
@Injectable({ providedIn: 'root' })
export class LoggingService {
  private readonly snackbar = inject(MatSnackBar);

  level = LoggingLevel.LOG_1;
  snackbarLoggingEnabled = false;

  constructor() {
    (window as unknown as { logger: LoggingService }).logger = this;
  }

  enableSnackbarLogging() {
    this.snackbarLoggingEnabled = true;
    this.log1('Snackbar logging enabled');
  }

  disable(): void {
    this.level = LoggingLevel.DISABLED;
  }

  enable(level: LoggingLevel): void {
    this.level = level;
  }

  log(level: LoggingLevel, ...args: any[]) {
    if (this.level === LoggingLevel.DISABLED) return;
    if (this.level >= level) {
      console.log(...args);
      if (this.snackbarLoggingEnabled) {
        this.snackbar.open('Log: ' + args.join(' '), 'Dismiss', {
          duration: undefined,
          panelClass: `logging-snackbar`,
        });
      }
    }
  }

  log1(...args: any[]): void {
    this.log(LoggingLevel.LOG_1, ...args);
  }

  log2(...args: any[]): void {
    this.log(LoggingLevel.LOG_2, ...args);
  }

  log3(...args: any[]): void {
    this.log(LoggingLevel.LOG_3, ...args);
  }

  fatal(...args: any[]): void {
    if (this.level === LoggingLevel.DISABLED) return;
    if (this.level >= LoggingLevel.FATAL) {
      console.error('FATAL:', ...args);
      if (this.snackbarLoggingEnabled) {
        this.snackbar.open('Fatal: ' + args.join(' '), 'Dismiss', {
          duration: undefined,
          panelClass: `logging-snackbar fatal`,
        });
      }
      throw new Error('Fatal error, see above.');
    }
  }

  error(...args: any[]): void {
    if (this.level === LoggingLevel.DISABLED) return;
    if (this.level >= LoggingLevel.ERROR) {
      console.error(...args);
      if (this.snackbarLoggingEnabled) {
        this.snackbar.open('Error: ' + args.join(' '), 'Dismiss', {
          duration: undefined,
          panelClass: `logging-snackbar error`,
        });
      }
    }
  }

  warn(...args: any[]): void {
    if (this.level === LoggingLevel.DISABLED) return;
    if (this.level >= LoggingLevel.WARN) {
      console.warn(...args);
      if (this.snackbarLoggingEnabled) {
        this.snackbar.open('Warn: ' + args.join(' '), 'Dismiss', {
          duration: undefined,
          panelClass: `logging-snackbar warn`,
        });
      }
    }
  }
}
